/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging/Log";

const TAG: string = "TizenKeys";

/** "present" / "missing" label used throughout the resolution logging below. */
const presence = (value: unknown): string =>
  value !== null && value !== undefined ? "present" : "missing";

/**
 * Look for UnityShell on the parent window.
 *
 * Split out of resolveUnityShell() so each function has a single lookup
 * strategy; the parent lookup is the one that can throw (cross-origin).
 */
const resolveParentUnityShell = (): Record<string, unknown> | undefined => {
  try {
    const isIframe =
      typeof window !== "undefined" &&
      window.parent !== null &&
      window.parent !== undefined &&
      window.parent !== window;
    Log.d(TAG, `resolveUnityShell() — isIframe = ${String(isIframe)}`);

    if (!isIframe) {
      return undefined;
    }

    const parentWin = window.parent as unknown as Record<string, unknown>;
    const parentShell = parentWin["UnityShell"] as
      | Record<string, unknown>
      | undefined;
    Log.d(
      TAG,
      `resolveUnityShell() — window.parent.UnityShell = ${presence(parentShell)}`
    );
    if (parentShell === null || parentShell === undefined) {
      return undefined;
    }

    const bridge = parentShell["Bridge"] as Record<string, unknown> | undefined;
    Log.d(
      TAG,
      `resolveUnityShell() — window.parent.UnityShell.Bridge = ${presence(bridge)}, ` +
        `registerFunctionKeys = ${bridge !== null && bridge !== undefined ? typeof bridge["registerFunctionKeys"] : "N/A"}`
    );
    return parentShell;
  } catch (error: unknown) {
    Log.e(
      TAG,
      "resolveUnityShell() — cross-origin access to parent window blocked",
      error
    );
    return undefined;
  }
};

/**
 * Resolve the UnityShell object from the current window or its parent.
 *
 * When the web app runs inside a shell iframe, `window.UnityShell` may be
 * undefined if the shell assigned it before the iframe finished loading
 * (the assignment is lost when the iframe navigates). In that case, fall
 * back to `window.parent.UnityShell` which is always available because
 * the shell sets it on its own (top-level) window.
 */
const resolveUnityShell = (
  win: Record<string, unknown>
): Record<string, unknown> | undefined => {
  // 1. Check current window (works if shell injected post-load)
  const local = win["UnityShell"] as Record<string, unknown> | undefined;
  Log.d(TAG, `resolveUnityShell() — window.UnityShell = ${presence(local)}`);
  if (local !== null && local !== undefined) {
    return local;
  }

  // 2. Check parent window (shell's own window — always has UnityShell)
  const parentShell = resolveParentUnityShell();
  if (parentShell !== undefined) {
    return parentShell;
  }

  Log.w(TAG, "resolveUnityShell() — UnityShell not found anywhere");
  return undefined;
};

/**
 * Samsung Tizen requires explicit registration before the app can
 * receive remote-specific key events (color buttons, media keys, etc.).
 *
 * Call this once during app initialization, before NavigationEngine.start().
 *
 * When running inside a shell-app iframe, the `tizen` global is not
 * directly available. In that case, we attempt to register via the
 * shell-injected `window.UnityShell.Bridge.registerFunctionKeys` which
 * retains a closure over the shell's `tizen` reference.
 */
export function registerTizenKeys(): void {
  if (typeof window === "undefined") {
    return;
  }

  const keys: string[] = [
    "MediaPlayPause",
    "MediaPlay",
    "MediaPause",
    "MediaStop",
    "MediaRewind",
    "MediaFastForward",
    "ColorF0Red",
    "ColorF1Green",
    "ColorF2Yellow",
    "ColorF3Blue",
    "ChannelUp",
    "ChannelDown",
    "Info",
    "Guide",
    "Exit"
  ];

  const win = window as unknown as Record<string, unknown>;

  // Direct access: tizen global is available (top-level or same-origin iframe)
  if (win["tizen"]) {
    try {
      const tizen = win["tizen"] as Record<string, unknown>;
      const inputDevice = tizen["tvinputdevice"] as
        | Record<string, unknown>
        | undefined;

      if (
        inputDevice &&
        typeof inputDevice["registerKeyBatch"] === "function"
      ) {
        (inputDevice["registerKeyBatch"] as (keys: string[]) => void)(keys);
        Log.d(TAG, "registerTizenKeys() — registered via tizen global");
      }
    } catch (error: unknown) {
      Log.e(TAG, "registerTizenKeys() — failed to register keys", error);
    }
    return;
  }

  // Fallback: running inside shell iframe — use shell-injected UnityShell bridge.
  // Try window.UnityShell first (shell injected post-load), then window.parent
  // (always available since UnityShell is set on the shell's top window).
  const unityShell = resolveUnityShell(win);
  if (unityShell !== null && unityShell !== undefined) {
    const bridge = unityShell["Bridge"] as Record<string, unknown> | undefined;
    if (
      bridge !== null &&
      bridge !== undefined &&
      typeof bridge["registerFunctionKeys"] === "function"
    ) {
      try {
        (bridge["registerFunctionKeys"] as (keys: string[]) => void)(keys);
        Log.d(
          TAG,
          "registerTizenKeys() — registered via UnityShell.Bridge fallback"
        );
      } catch (error: unknown) {
        Log.e(
          TAG,
          "registerTizenKeys() — UnityShell.Bridge fallback failed",
          error
        );
      }
      return;
    }
  }

  Log.w(
    TAG,
    "registerTizenKeys() — neither tizen global nor UnityShell.Bridge available"
  );
}
